'use client';

import cn from '@sindresorhus/class-names';
import {escapeForJsContext} from '@utils/stringEscape';
import Link from 'next/link';
import {usePathname} from 'next/navigation';
import {memo, useCallback, useMemo} from 'react';
import {BreadcrumbList, ListItem, WithContext} from 'schema-dts';

import {DOMAIN} from '@web-catalog/const';

import {Text} from '@web-catalog/ui-kit/Text';

import {removeTrailingSlash} from '@web-catalog/utils';
import {useBaseAnalytics} from '@web-catalog/utils/analytics/useBaseAnalytics';

import {Icon} from '../Icon';
import {ChevronRightSmallSvg} from '../SvgIcons';
import styles from './Breadcrumbs.module.scss';

export type BreadcrumbsBackstackItem = {
  path: string;
  title: string;
};

export type BreadcrumbsProps = {
  backstack: Array<BreadcrumbsBackstackItem>;
  currentLocationTitle: string;
  className?: string;
  onLinkClick?: (item: BreadcrumbsBackstackItem, index: number) => void;
};

const BreadcrumbItem = ({
  item,
  position,
  isLast = false,
  onLinkClick,
}: {
  item: BreadcrumbsBackstackItem;
  position: number;
  isLast?: boolean;
  onLinkClick?: BreadcrumbsProps['onLinkClick'];
}) => {
  const onClick = useCallback(() => {
    onLinkClick?.(item, position);
  }, [onLinkClick, item, position]);

  return (
    <li
      itemProp="itemListElement"
      itemScope
      itemType="https://schema.org/ListItem"
      className={styles.listItem}
      data-testid={`breadcrumbs-${position}`}
    >
      <Link
        href={item.path}
        className={cn(styles.link, isLast && styles.disabled)}
        onClick={onClick}
        itemProp="item"
        aria-current={isLast && 'page'}
        prefetch={false}
      >
        <Text
          color={isLast ? 'neutral-500' : 'primary-main'}
          typography="control-s"
          className={styles.linkText}
          itemProp="name"
          overflow="ellipsis"
          maxLines={1}
        >
          {item.title}
        </Text>
        <meta itemProp="position" content={`${position}`} />
      </Link>
      {!isLast && (
        <Icon svg={ChevronRightSmallSvg} className={styles.chevron} />
      )}
    </li>
  );
};

export const Breadcrumbs = memo<BreadcrumbsProps>(
  ({backstack, currentLocationTitle, onLinkClick, className}) => {
    const path = usePathname() ?? '';
    const {logBreadcrumbsItemClick} = useBaseAnalytics();

    const jsonLdData = useMemo(() => {
      const data: WithContext<BreadcrumbList> = {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          ...backstack.map(
            ({path, title}, index): ListItem => ({
              '@type': 'ListItem',
              item: {
                '@id': removeTrailingSlash(`${DOMAIN}${path}`),
                name: escapeForJsContext(title),
              },
              position: index,
            }),
          ),
          {
            '@type': 'ListItem',
            item: {
              '@id': removeTrailingSlash(`${DOMAIN}${path}`),
              name: escapeForJsContext(currentLocationTitle),
            },
            position: backstack.length,
          },
        ],
      };

      return JSON.stringify(data);
    }, [backstack, path, currentLocationTitle]);

    const onLinkClickWithAnalytics = useCallback(
      (item: BreadcrumbsBackstackItem, index: number) => {
        logBreadcrumbsItemClick(item.title);
        onLinkClick?.(item, index);
      },
      [logBreadcrumbsItemClick, onLinkClick],
    );

    const rootItem = useMemo<BreadcrumbsBackstackItem>(
      () => ({
        title: currentLocationTitle,
        path,
      }),
      [currentLocationTitle, path],
    );

    return (
      <>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{__html: jsonLdData}}
        />
        <ul
          itemScope
          itemType="https://schema.org/BreadcrumbList"
          className={cn(styles.list, className)}
          aria-label="Навигация"
        >
          {backstack.map((item, index) => (
            <BreadcrumbItem
              key={item.path}
              item={item}
              position={index}
              onLinkClick={onLinkClickWithAnalytics}
            />
          ))}
          <BreadcrumbItem
            item={rootItem}
            position={backstack.length}
            onLinkClick={onLinkClickWithAnalytics}
            isLast
          />
        </ul>
      </>
    );
  },
);

Breadcrumbs.displayName = 'Breadcrumbs';
